import { useState, useRef, ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './DailyLogPage.css';
import '../pages/Landing.css';

/* ============================================================================
   Mock data — representative example for demo purposes
   ============================================================================ */

interface LogEntry {
  id: string;
  content: string;
}

const MOCK_DATE = 'Monday, June 9';

const MOCK_NUTRITION: Record<string, LogEntry[]> = {
  breakfast: [
    { id: 'b1', content: 'Greek yogurt with granola and blueberries' },
  ],
  lunch: [
    { id: 'l1', content: 'Grilled chicken salad with avocado, cucumber, and olive oil dressing' },
  ],
  dinner: [],
  snack: [
    { id: 's1', content: 'Apple with almond butter' },
  ],
};

const MOCK_EXERCISE: LogEntry[] = [
  { id: 'e1', content: '35 min run at moderate pace, about 3.2 miles' },
];

const MOCK_MIND: LogEntry[] = [
  { id: 'm1', content: '7.5 hours sleep, woke up feeling rested' },
  { id: 'm2', content: 'Energy levels good in the morning, dipped a bit around 3pm' },
];

/* ============================================================================
   Shell
   ============================================================================ */

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="daily-log-page">
      <PublicHeader />
      {children}
      <footer className="daily-log-footer">
        <div className="daily-log-container daily-log-footer-inner">
          <div className="daily-log-footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="daily-log-footer-logo" />
            <span>mosaime</span>
          </div>
          <p className="daily-log-footer-copy">
            &copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.
          </p>
          <div style={{ display: 'flex', gap: '16px', fontSize: '0.8rem' }}>
            <Link to="/how-it-works" style={{ color: '#888', textDecoration: 'none' }}>How It Works</Link>
            <Link to="/login" style={{ color: '#888', textDecoration: 'none' }}>Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============================================================================
   EditableEntry — local state only (demo: no API call)
   ============================================================================ */

interface EditableEntryProps {
  entry: LogEntry;
  onUpdate: (id: string, newContent: string) => void;
}

function EditableEntry({ entry, onUpdate }: EditableEntryProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.content);
  const [saved, setSaved] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      taRef.current.style.height = 'auto';
      taRef.current.style.height = `${taRef.current.scrollHeight}px`;
    }
  }, [editing]);

  function handleSave() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onUpdate(entry.id, trimmed);
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (editing) {
    return (
      <li className="dl-entry dl-entry-editing">
        <div className="dl-entry-edit-wrap">
          <textarea
            ref={taRef}
            className="dl-add-textarea dl-entry-edit-ta"
            value={draft}
            onChange={e => {
              setDraft(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            rows={2}
          />
          <div className="dl-entry-edit-actions">
            <button className="dl-add-btn" onClick={handleSave} disabled={!draft.trim()}>
              Save
            </button>
            <button
              className="dl-entry-cancel-btn"
              onClick={() => { setDraft(entry.content); setEditing(false); }}
            >
              Cancel
            </button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="dl-entry">
      <span className="dl-entry-dot" />
      <div className="dl-entry-text">
        {entry.content}
        {saved && <div className="dl-entry-meta" style={{ color: '#2e7d32' }}>Saved (demo)</div>}
      </div>
      <button
        className="dl-entry-edit-btn"
        onClick={() => setEditing(true)}
        aria-label="Edit entry"
      >
        Edit
      </button>
    </li>
  );
}

/* ============================================================================
   LocalAddForm — demo-only, entries stay in local state
   ============================================================================ */

function LocalAddForm({
  placeholder,
  label,
  onAdded,
}: {
  placeholder: string;
  label: string;
  onAdded: (content: string) => void;
}) {
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    onAdded(trimmed);
    setText('');
    setSaved(true);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setTimeout(() => setSaved(false), 2000);
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  }

  return (
    <form className="dl-add-form" onSubmit={handleSubmit}>
      <span className="dl-add-label">{label}</span>
      <div className="dl-add-row">
        <textarea
          ref={textareaRef}
          className="dl-add-textarea"
          placeholder={placeholder}
          value={text}
          onChange={handleInput}
          rows={1}
        />
        <button className="dl-add-btn" type="submit" disabled={!text.trim()}>
          Add
        </button>
      </div>
      {saved && <p className="dl-add-success">Saved! (demo — not persisted)</p>}
    </form>
  );
}

/* ============================================================================
   MealSubsection
   ============================================================================ */

function MealSubsection({
  label,
  initialEntries,
}: {
  label: string;
  mealType?: string;
  initialEntries: LogEntry[];
}) {
  const [items, setItems] = useState<LogEntry[]>(initialEntries);

  return (
    <div className="dl-subsection">
      <div className="dl-subsection-head">
        <p className="dl-subsection-label">{label}</p>
        {items.length > 0 && (
          <span className="dl-subsection-count">{items.length} logged</span>
        )}
      </div>

      {items.length === 0 && <p className="dl-empty">Nothing logged yet</p>}

      {items.length > 0 && (
        <ul className="dl-entry-list">
          {items.map(e => (
            <EditableEntry
              key={e.id}
              entry={e}
              onUpdate={(id, newContent) =>
                setItems(prev => prev.map(item => item.id === id ? { ...item, content: newContent } : item))
              }
            />
          ))}
        </ul>
      )}

      <LocalAddForm
        placeholder={`What did you have for ${label.toLowerCase()}?`}
        label={items.length === 0 ? 'Add entry' : 'Add another'}
        onAdded={content =>
          setItems(prev => [...prev, { id: `local-${Date.now()}`, content }])
        }
      />
    </div>
  );
}

/* ============================================================================
   CategorySection — Movement or Mind
   ============================================================================ */

function CategorySection({
  sectionLabel,
  initialEntries,
  placeholder,
  addLabel,
}: {
  sectionLabel: string;
  initialEntries: LogEntry[];
  placeholder: string;
  addLabel: string;
}) {
  const [items, setItems] = useState<LogEntry[]>(initialEntries);

  return (
    <div className="dl-subsection">
      <div className="dl-subsection-head">
        <p className="dl-subsection-label">{sectionLabel}</p>
        {items.length > 0 && (
          <span className="dl-subsection-count">{items.length} logged</span>
        )}
      </div>

      {items.length === 0 && <p className="dl-empty">Nothing logged yet</p>}

      {items.length > 0 && (
        <ul className="dl-entry-list">
          {items.map(e => (
            <EditableEntry
              key={e.id}
              entry={e}
              onUpdate={(id, newContent) =>
                setItems(prev => prev.map(item => item.id === id ? { ...item, content: newContent } : item))
              }
            />
          ))}
        </ul>
      )}

      <LocalAddForm
        placeholder={placeholder}
        label={addLabel}
        onAdded={content =>
          setItems(prev => [...prev, { id: `local-${Date.now()}`, content }])
        }
      />
    </div>
  );
}

/* ============================================================================
   Page
   ============================================================================ */

export function DailyLogDemoPage() {
  return (
    <Shell>
      {/* Demo banner */}
      <div
        style={{
          background: '#fff3cd',
          borderBottom: '1px solid #ffc107',
          padding: '10px 20px',
          textAlign: 'center',
          fontSize: '0.85rem',
          color: '#856404',
          fontWeight: 500,
        }}
      >
        Demo — entries added or edited here are not saved. Real links are sent via SMS at 7:30 PM.
      </div>

      {/* Hero */}
      <section className="daily-log-hero">
        <div className="daily-log-container daily-log-hero-inner">
          <p className="daily-log-label">Daily Wellness Log</p>
          <h1 className="daily-log-title">{MOCK_DATE}</h1>
          <p className="daily-log-greeting">
            Good evening! Here&rsquo;s everything logged today. Tap Edit on any entry to correct it, or add new ones below.
          </p>
        </div>
      </section>

      {/* Body */}
      <main className="daily-log-body">
        <div className="daily-log-container">

          {/* Nutrition */}
          <section className="dl-section">
            <div className="dl-section-header">
              <span className="dl-section-icon" aria-hidden>&#x1F955;</span>
              <h2 className="dl-section-title">Nutrition</h2>
            </div>
            <MealSubsection label="Breakfast" mealType="breakfast" initialEntries={MOCK_NUTRITION.breakfast} />
            <MealSubsection label="Lunch"     mealType="lunch"     initialEntries={MOCK_NUTRITION.lunch}     />
            <MealSubsection label="Dinner"    mealType="dinner"    initialEntries={MOCK_NUTRITION.dinner}    />
            <MealSubsection label="Snacks"    mealType="snack"     initialEntries={MOCK_NUTRITION.snack}     />
          </section>

          {/* Movement */}
          <section className="dl-section">
            <div className="dl-section-header">
              <span className="dl-section-icon" aria-hidden>&#x1F3C3;</span>
              <h2 className="dl-section-title">Movement</h2>
            </div>
            <CategorySection
              sectionLabel="Activity"
              initialEntries={MOCK_EXERCISE}
              placeholder="e.g. 30 min walk, yoga class, strength workout..."
              addLabel={MOCK_EXERCISE.length === 0 ? 'Add movement' : 'Add another'}
            />
          </section>

          {/* Mind */}
          <section className="dl-section">
            <div className="dl-section-header">
              <span className="dl-section-icon" aria-hidden>&#x1F9E0;</span>
              <h2 className="dl-section-title">Mind</h2>
            </div>
            <CategorySection
              sectionLabel="Mind & Sleep"
              initialEntries={MOCK_MIND}
              placeholder="e.g. 7.5 hrs sleep, feeling energized, a bit stressed today..."
              addLabel={MOCK_MIND.length === 0 ? 'Add mood or sleep' : 'Add another'}
            />
          </section>

        </div>
      </main>
    </Shell>
  );
}

export default DailyLogDemoPage;
